// web/src/main.tsx — MsalProvider wrap + QueryClientProvider + Router + post-login ?next= restore (D14 §1, D16).
// initAuth() runs MSAL initialize()+handleRedirectPromise() before render in real mode; in mock it's a no-op so
// the app boots with NO Entra config (see-it-first).
import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import { ToastHost } from './components/Toast';
import { MsalProvider } from './lib/MsalProvider';
import { msal, initAuth, isSignedIn } from './lib/auth';
import { initThemeWatcher } from './lib/theme';
import './index.css';

const qc = new QueryClient({ defaultOptions: { queries: { refetchOnWindowFocus: false, staleTime: 5000 } } });

// After a fresh sign-in, MSAL lands us back on the app root. Replay the route the visitor
// asked for before the Entra round-trip (Login.tsx stashed it in sessionStorage). Once only.
function honorNextAfterLogin(): void {
  if (!isSignedIn()) return;
  const next = sessionStorage.getItem('next');
  if (!next) return;
  sessionStorage.removeItem('next');
  const here = window.location.pathname + window.location.search;
  if (next !== here) window.history.replaceState(null, '', next);
}

async function bootstrap(): Promise<void> {
  initThemeWatcher();      // keep 'System' appearance in sync with OS light/dark changes
  await initAuth();        // initialize() + handleRedirectPromise() — no-op in mock mode
  honorNextAfterLogin();   // replay ?next= into the URL before React reads the route
  createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <MsalProvider instance={msal}>
        <QueryClientProvider client={qc}>
          <BrowserRouter>
            <App />
            <ToastHost />
          </BrowserRouter>
        </QueryClientProvider>
      </MsalProvider>
    </React.StrictMode>,
  );
}

void bootstrap();
